import { Trans, useTranslation } from 'react-i18next';
import { sqWorksheetStore } from '@/core/core.stores';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { formatTime } from '@/datetime/dateTime.utilities';
import { faLocationDot, faLock } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Badge, Icon } from '@seeqdev/qomponents';
import type { WorkbookApprovalFlowStep } from '@/worksheet/worksheet.types';

type ApprovalFlowStepItemProps = {
  step: WorkbookApprovalFlowStep;
  isCurrentStep: boolean;
  itemType: string;
};

export const ApprovalFlowStepItem = ({ step, isCurrentStep, itemType }: ApprovalFlowStepItemProps) => {
  const { t } = useTranslation();
  const { timezone } = useFluxPaths(sqWorksheetStore, ['timezone']);
  const { approval } = step;
  const approvedAt = approval ? formatTime(approval.approvedAt, timezone, 'LLL') : undefined;

  return (
    <li className="tw:gap-1" data-testid={`applyApprovalFlowStep-${step.id}`}>
      <span>{step.name}</span>
      {step.lockRequired ? (
        <Icon
          icon={faLock}
          extraClassNames="tw:ml-1"
          testId={`applyApprovalFlowStepLock-${step.id}`}
          tooltip={t('APPROVAL_FLOWS.LOCK_REQUIRED', { itemType })}
          aria-label={t('APPROVAL_FLOWS.LOCK_REQUIRED', { itemType })}
        />
      ) : null}
      {isCurrentStep ? (
        <Badge
          variant="neutral"
          extraClassNames="tw:relative tw:top-px tw:ml-2 tw:border-0! tw:px-1! tw:py-0! tw:text-xs! tw:leading-4"
          testId={`applyApprovalFlowCurrentStep-${step.id}`}>
          <Icon icon={faLocationDot} type="inherit" extraClassNames="tw:mr-0.5" />
          {t('APPROVAL_FLOWS.CURRENT_STEP')}
        </Badge>
      ) : null}
      {approval ? (
        <span className="tw:text-sq-darkish-gray" data-testid={`applyApprovalFlowStepApproval-${step.id}`}>
          {' – '}
          <Trans
            i18nKey="APPROVAL_FLOWS.APPROVED_BY"
            values={{ approverName: approval.approver.name, approvedAt }}
            components={{
              Approver: (
                <span data-qtip-text={approval.approver.email} data-testid={`applyApprovalFlowStepApprover-${step.id}`}>
                  {approval.approver.name}
                </span>
              ),
            }}
          />
        </span>
      ) : null}
    </li>
  );
};
